import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDetermineMobile } from '@myrealtrip/device-detect';
import GlobalSearch from '../../../shared/components/GlobalSearch/LazyGlobalSearch';

import { css } from '@emotion/css';
import { BenefitPageUrl } from '../../../constants/header';
import { useEnvContext } from '../../../modules/EnvProvider';
import DomainManager from '../../../shared/utils/DomainManager';
import { useLayoutHeaderContext } from '../../../modules/LayoutHeaderContext';
import useGetPlaceholder from '../../../shared/components/GlobalSearch/hooks/apis/useGetPlaceholder';
import { WeblogPageCategory, WeblogScreenName } from '../../../shared/constants/weblog';
import { COLOR, LAYOUT } from '../../../shared/types/globalSearch';
import media from '../../../shared/utils/mediaQuery';
import { sendClickLog } from '../../utils/sendClickLog';
import BenefitButton from '../BenefitButton';
import GlobalNavItems from '../GlobalNavItems';
import GnbLogo from '../GnbLogo';
import { HeaderRegion } from '../../../data/types';
import { RegionChipToggle } from '../RegionChipToggle';
import SearchButton from '../SearchButton';
import SideBar from '../SideBar';
import SideBarButton from '../SideBarButton';

export default function GNB() {
  const {
    theme,
    mainVerticalItems,
    isMainPage,
    region,
    onRegionChange,
    regionToggleBadge,
    resolvedRegion: contextResolvedRegion,
    regionResolved,
  } = useLayoutHeaderContext();
  const isMobile = useDetermineMobile();
  const { targetEnv } = useEnvContext();
  const [isExperiencesPage, setIsExperiencesPage] = useState(false);
  const benefitLNBCategory = useMemo(
    () => mainVerticalItems?.find((item) => item.link.mobile_web.includes(BenefitPageUrl)),
    [mainVerticalItems],
  );

  const [isSideBarOpen, setIsSideBarOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);

  useEffect(() => {
    const pathname = window.location.pathname;
    const isExperiences = pathname === '/experiences' || pathname === '/experiences/';
    setIsExperiencesPage(isExperiences);
  }, []);

  // PST-570 — region 파생·연속성 쿠키(read/write)는 부모 프로바이더(LayoutHeaderContext)가 단독 담당한다.
  // GNB 는 항상 그 프로바이더 안에서 렌더되므로(MrtHeader→Header→GNB) contextResolvedRegion 을 그대로 소비한다.

  /*
   * ⚠️ 검색 아이콘이 검색홈으로 이동하게 되면서 이 토글을 켜는 곳이 없어졌다. 아래 모바일 뷰의
   * `GlobalSearch` 는 `isSuggestionDialogOpen` 이 항상 `false` 라 **열리지 않는 죽은 코드**다.
   * 되돌리기가 한 줄(`handleOpenSearchPopup` 에서 이 토글을 켜면 된다)이라 남겨 둔다.
   */
  const handleSearchDialogToggle = (isOpen: boolean) => setIsSearchDialogOpen(isOpen);

  /*
   * 검색 아이콘 → **검색홈 페이지로 이동**한다(기존: `GlobalSearch` 다이얼로그).
   * 메인홈 돋보기·검색결과 검색바와 같은 목적지로 맞춘다.
   *
   * ⚠️ **절대 URL 이어야 한다.** GNB 는 `experiences.`·`accommodation.` 등 여러 서브도메인에서
   * 렌더되는데, 상대경로 `/search/home` 으로 가면 그 서브도메인의 없는 경로로 떨어져 404 가 된다.
   * 바로 아래 `navigateToRegionHome` 이 같은 이유로 `DomainManager` 를 쓴다.
   *
   * region 은 헤더 컨텍스트가 이미 풀어 둔 값을 그대로 넘겨, 국내를 보던 사용자가 해외 검색홈으로
   * 떨어지지 않게 한다.
   *
   * 로그는 이동 **전에** 남긴다 — 페이지가 넘어가면 전송 기회가 없다.
   */
  const handleOpenSearchPopup = () => {
    if (typeof window === 'undefined') {
      return;
    }

    sendClickLog({
      itemKind: 'button',
      screenName: WeblogScreenName.COMMON,
      pageCategory: WeblogPageCategory.COMMON,
      eventName: 'search_init_button',
      itemName: 'search_init_button',
    });

    const travelerDomain = new DomainManager(targetEnv).getTravelerServiceDomain();
    window.location.assign(`${travelerDomain}/search/home?region=${resolvedRegion}`);
  };
  const handleOpenSideBar = () => setIsSideBarOpen(true);
  const handleCloseSideBar = () => setIsSideBarOpen(false);

  const { placeholder } = useGetPlaceholder();

  const renderMainHeader = () => {
    return (
      <Container>
        <MainLogoWrapper>
          <GnbLogo />
        </MainLogoWrapper>
        <MainHeaderWrapper>
          {benefitLNBCategory ? (
            <BenefitButton link={benefitLNBCategory.link.mobile_web} />
          ) : (
            <SearchButton
              onClick={handleOpenSearchPopup}
              className={css`
                padding: 8px 10px !important;
              `}
            />
          )}
          <SideBarButton
            onClick={handleOpenSideBar}
            className={css`
              padding: 8px 16px 8px 8px !important;
            `}
          />
        </MainHeaderWrapper>
        <GlobalSearch
          isShowSearchInput={false}
          isSuggestionDialogOpen={isSearchDialogOpen}
          onSuggestionDialogToggle={handleSearchDialogToggle}
          placeholder={placeholder}
        />
        {isSideBarOpen && (
          <SideBar isOpen={isSideBarOpen} direction="right" onClose={handleCloseSideBar} />
        )}
      </Container>
    );
  };

  const renderDefaultHeader = () => {
    return (
      <Container>
        <SideBarButton onClick={handleOpenSideBar} />
        <GnbLogo />
        <SearchButton onClick={handleOpenSearchPopup} />

        <GlobalSearch
          isShowSearchInput={false}
          isSuggestionDialogOpen={isSearchDialogOpen}
          onSuggestionDialogToggle={handleSearchDialogToggle}
          placeholder={placeholder}
        />
        {isSideBarOpen && <SideBar isOpen={isSideBarOpen} onClose={handleCloseSideBar} />}
      </Container>
    );
  };

  // PST-570 — region 값/콜백은 rules-of-hooks 상 조건부 return(아래 isMobile) 위에서 계산한다.
  //  리사이즈로 isMobile 이 토글되면 early return 뒤의 useCallback 호출 개수가 렌더마다 달라져
  //  React #300(Rendered fewer hooks than expected)이 발생하므로, 훅은 반드시 early return 위에서 호출한다.
  const resolvedRegion = contextResolvedRegion ?? region ?? 'overseas';
  //  ⚠️ experiences. 등 서브도메인에서 상대경로 '/' 로 이동하면 그 서브도메인 루트(404)로 가므로,
  //  DomainManager 로 targetEnv 별 www 절대 URL 을 만들어 이동한다. domestic→?isDomestic=true.
  const navigateToRegionHome = useCallback(
    (next: HeaderRegion) => {
      if (typeof window === 'undefined') return;
      const travelerDomain = new DomainManager(targetEnv).getTravelerServiceDomain();
      window.location.assign(
        next === 'domestic' ? `${travelerDomain}/?isDomestic=true` : `${travelerDomain}/`,
      );
    },
    [targetEnv],
  );
  const resolvedOnRegionChange = onRegionChange ?? navigateToRegionHome;
  // 모바일 RegionToggle 과 동일한 region_button 클릭 bizlog(PC 파리티). onChange(주입 setRegion / 기본
  // navigateToRegionHome) 실행 전에 helper 로 즉시 전송해 full 네비게이션에도 유실되지 않게 한다.
  // screen: 메인홈=main/main_domestic(모바일 정본과 동일), 그 외 화면=common.
  const handleRegionChange = useCallback(
    (next: HeaderRegion) => {
      sendClickLog({
        itemKind: 'button',
        screenName: isMainPage
          ? resolvedRegion === 'domestic'
            ? 'main_domestic'
            : 'main'
          : WeblogScreenName.COMMON,
        pageCategory: isMainPage ? WeblogPageCategory.MAIN : WeblogPageCategory.COMMON,
        eventName: 'region_button',
        itemName: 'region_button',
        regionType: next,
      });
      resolvedOnRegionChange(next);
    },
    [isMainPage, resolvedRegion, resolvedOnRegionChange],
  );

  if (isMobile) {
    return isMainPage ? renderMainHeader() : renderDefaultHeader();
  }

  const globalSearchNode = (
    <GlobalSearch
      variations={{
        layout: LAYOUT.header,
        color: theme === 'white' ? COLOR.white : COLOR.transparent,
      }}
      placeholder={placeholder}
    />
  );

  const regionNode = (
    <RegionChipToggle
      region={resolvedRegion}
      onChange={handleRegionChange}
      badgeText={regionToggleBadge}
      regionResolved={regionResolved}
    />
  );

  // PST-570 — 데스크톱 메인 홈: 단일행 헤더(Figma 1702-24753).
  //  로고 + 해외/국내 토글 (좌) · 검색바(가운데 345px) · GlobalNavItems (우).
  //  LNB 카테고리 행은 Header 가 GNB 아래에 별도로 렌더한다.
  if (isMainPage) {
    return (
      <MainContainer>
        <MainTopLeft>
          <GnbLogo />
          <RegionToggleWrapper>{regionNode}</RegionToggleWrapper>
        </MainTopLeft>
        {/* TnA 홈에는 콘텐츠에 글로벌 검색 UI가 존재하므로 노출하지 않는다 */}
        {!isExperiencesPage && <MainSearchCentered>{globalSearchNode}</MainSearchCentered>}
        <MainNavRight>
          <GlobalNavItems />
        </MainNavRight>
      </MainContainer>
    );
  }

  // PST-570 — 비홈 데스크톱도 메인과 동일한 3분할(좌 flex:1 · 검색 가운데 345px 고정 · 우 flex:1)로
  // 검색창 위치를 메인 홈과 일치시킨다.
  return (
    <MainContainer>
      <MainTopLeft>
        <GnbLogo />
        {/* PST-570 — 비홈 화면: 로고 옆 region 토글(주입/URL 없으면 해외 default). */}
        <RegionToggleWrapper>{regionNode}</RegionToggleWrapper>
      </MainTopLeft>
      {/* TnA 홈에는 콘텐츠에 글로벌 검색 UI가 존재하므로 노출하지 않는다 */}
      {!isExperiencesPage && <MainSearchCentered>{globalSearchNode}</MainSearchCentered>}
      <MainNavRight>
        <GlobalNavItems />
      </MainNavRight>
    </MainContainer>
  );
}

const Container = styled.nav`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 72px;
  max-width: 1060px;
  margin: 0 auto;

  ${media.mobile} {
    justify-content: space-between;
    height: 48px;
  }
`;

// PST-570 — 데스크톱 메인 홈 단일행 헤더(검색바 상단 가운데). 로고+토글(좌) · 검색(가운데) · GlobalNavItems(우).
const MainContainer = styled.nav`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1060px;
  height: 72px;
  margin: 0 auto;
`;

// 좌: 로고+토글 (콘텐츠 폭). 검색바가 좌/우 그룹 사이 정중앙(양쪽 거리 동일)에 오도록 좌우 그룹은
// flex:0 0 auto(내용 폭)로 두고, 가운데 검색바가 남은 공간을 채우며 중앙 정렬한다.
const MainTopLeft = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

// 우: GlobalNavItems (콘텐츠 폭). 좌측과 함께 내용 폭 → 가운데 검색바가 좌/우 그룹 사이 중앙.
const MainNavRight = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

// PST-570 — 검색바(345px). 좌/우 그룹(내용 폭) 사이 남은 공간을 채우고 그 안에서 중앙 정렬 →
// 검색바 좌측(토글)까지 거리 == 우측(GlobalNavItems)까지 거리. Figma 1702-24753.
// transform 미사용 → stacking context 를 만들지 않아 검색 자동완성 팝업이 LNB 위로 정상 노출된다.
const MainSearchCentered = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  justify-content: center;

  /* GlobalSearch(desktop) 를 345px 로 고정하고 남은 공간 중앙에 배치(우측 14px 마진 제거). */
  & > div {
    width: 345px;
    max-width: 100%;
    margin-right: 0;
  }
`;

const RegionToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;
`;

const MainLogoWrapper = styled.div`
  padding: 10px 16px;
`;

const MainHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
`;
